import { useCallback, useEffect, useState } from 'react'
import type { CredencialDoHsm } from '@/api/cloudhsm'

/**
 * Sessão de gestão do HSM (G1 da spec F6, decidido em 04/11/2026): o admin digita a
 * credencial do HSM uma vez e ela fica **só no estado React desta aba** por 10 minutos, indo
 * junto em cada chamada. Nada de localStorage, sessionStorage, URL, chave de cache ou log:
 * fechar a aba, sair da página, clicar em "Encerrar" ou passar dos 10 minutos a descarta.
 */
export const DURACAO_DA_SESSAO_MS = 10 * 60_000

export interface SessaoDoHsm {
  /**
   * Muda a cada abertura. É o que separa o cache de uma sessão do da seguinte, sem pôr o
   * usuário ou a senha na chave da query.
   */
  id: number
  credencial: CredencialDoHsm
  expiraEm: number
}

export interface ControleDaSessao {
  sessao: SessaoDoHsm | null
  restanteMs: number
  abrir: (credencial: CredencialDoHsm) => void
  encerrar: () => void
}

let ultimoId = 0

export function useSessaoDoHsm(): ControleDaSessao {
  const [sessao, setSessao] = useState<SessaoDoHsm | null>(null)
  const [agora, setAgora] = useState(() => Date.now())

  const encerrar = useCallback(() => setSessao(null), [])
  const abrir = useCallback((credencial: CredencialDoHsm) => {
    const t = Date.now()
    ultimoId += 1
    setAgora(t)
    setSessao({ id: ultimoId, credencial, expiraEm: t + DURACAO_DA_SESSAO_MS })
  }, [])

  useEffect(() => {
    if (!sessao) return
    // O relógio confere a hora de verdade a cada tique: aba em segundo plano atrasa o
    // intervalo, mas não estica a sessão.
    const relogio = setInterval(() => {
      const t = Date.now()
      if (t >= sessao.expiraEm) setSessao(null)
      else setAgora(t)
    }, 1000)
    // `pagehide` cobre o bfcache, que guardaria a página (e a credencial) para o "voltar".
    window.addEventListener('pagehide', encerrar)
    return () => {
      clearInterval(relogio)
      window.removeEventListener('pagehide', encerrar)
    }
  }, [sessao, encerrar])

  return { sessao, restanteMs: sessao ? Math.max(0, sessao.expiraEm - agora) : 0, abrir, encerrar }
}
